/**
 * Avatar for a tutor on the gamification leaderboard.
 */
import * as React from 'react';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import {
  TUTOR_AVATAR_BOX_CLASS,
  TUTOR_AVATAR_ICON_CLASS,
  type TutorAvatarSize,
} from '@/components/gamification/gamification-configs';

/** Props for {@link TutorLeaderboardAvatar}. */
export interface TutorLeaderboardAvatarProps {
  photoUrl?: string | null;
  displayName?: string;
  size?: TutorAvatarSize;
  className?: string;
}

/** Photo when `photoUrl` loads; user-icon fallback on missing URL or image error. */
export const TutorLeaderboardAvatar: React.FC<TutorLeaderboardAvatarProps> = ({
  photoUrl,
  displayName = getRoleLabel('tutor'),
  size = 'md',
  className,
}) => {
  const [imageError, setImageError] = React.useState(false);
  const trimmedPhoto = photoUrl?.trim();

  function handleImageError(): void {
    setImageError(true);
  }

  if (trimmedPhoto && !imageError) {
    return (
      <img
        src={trimmedPhoto}
        alt={displayName}
        className={cn(
          'shrink-0 object-cover border border-border bg-muted',
          TUTOR_AVATAR_BOX_CLASS[size],
          className,
        )}
        onError={handleImageError}
      />
    );
  }

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center bg-muted text-muted-foreground',
        TUTOR_AVATAR_BOX_CLASS[size],
        className,
      )}
      aria-hidden
    >
      <User className={TUTOR_AVATAR_ICON_CLASS[size]} strokeWidth={2} />
    </span>
  );
};
